import React, { useEffect, useState } from 'react'
import { CashJournalValues, JournalVariation } from './Types'
import {
  DistributionConstraints,
  ContributionConstraints,
  DistributionReasonInfo,
  AccountDetails,
  RetirementConstraintsResponse
} from '@Types'
import isEmpty from 'lodash.isempty'
import { useFormatMessage, useFormatNumber } from '../../utils/intlHelpers'
import IraDistributionForm from '../../components/IraDistributionForm'
import { AccountLookupDetails } from '../AccountLookupDetails'
import { IratoIraForm } from './IraToIraForm'
import { Button, LabeledInput, Alert } from '@apex/component-library'
import { Text, Box, Stack, InputProps, CheckboxProps } from '@chakra-ui/react'
import TransferFrequency from '../TransferFrequency'
import { FromAccount } from '../FromAccount'
import { FormikTouched, useFormikContext } from 'formik'
import { TransferAmount } from '../TransferAmount'
import numberInputFormatter from '../../utils/inputFormatters'
import IraContributionForm from '../IraContributionForm'
import TransferFees from '../TransferFees'
import { FeeTransferType, TransferFeeDescriptionType } from '../TransferFees/types'
import useGetIraConstraints from '../../api/useGetIRAConstraints'
import { TransferMechanism, CashDirection } from 'apex-ui-services'
import { COLUMN_WIDTH, MAX_COLUMN_WIDTH, MAX_FORM_WIDTH } from '../../utils'

export interface CashJournalFormProps {
  handleBlur: (field: string) => void
  handleChange: (e: React.FormEvent<HTMLInputElement>, data: InputProps | CheckboxProps) => void
  onSubmit: () => void
  setFieldValue: (field: string, value: any) => void
  touched?: FormikTouched<CashJournalValues>
  values: CashJournalValues
  accountFromDisplay: string
  iraDistributionConstraints?: DistributionConstraints | null
  iraContributionConstraints?: ContributionConstraints | null
  formVariation?: JournalVariation
  handleLookupChange: (e: React.ChangeEvent, data: string) => void
  loading: boolean
  onLookupSubmit: (acc: string) => void
  handleChangeIRA: (key: string, value: any, section?: string) => void
  warningMessage?: string
  warningMessageTitle?: string
  accountLookupData?: AccountDetails
  accountLookUpError?: string
  handleSaveData: (fieldName: string, value: string) => void
  isNextDisabled: boolean
  setIsModalShowing: (isOpen: boolean) => void
  iraConstraints?: RetirementConstraintsResponse
}
export const CashJournalForm = ({
  accountFromDisplay,
  iraDistributionConstraints,
  iraContributionConstraints,
  formVariation,
  accountLookUpError,
  loading,
  onLookupSubmit,
  warningMessage,
  warningMessageTitle,
  touched,
  values,
  handleSaveData,
  accountLookupData,
  isNextDisabled,
  iraConstraints,
  setIsModalShowing
}: CashJournalFormProps) => {
  const formik = useFormikContext<CashJournalValues>()
  const formatMessage = useFormatMessage()
  const totalBalanceNotAllowed =
    !!iraDistributionConstraints && !iraDistributionConstraints?.totalDistributionAllowed && values.totalBalanceCheckbox
  const { data: fromAccountIraConstraints } = useGetIraConstraints(
    'cashJournalFromAccount',
    TransferMechanism.Journal,
    values.fromAccount!,
    CashDirection.Withdrawal
  )

  const numberFormatter = useFormatNumber()
  const [applicableFees, setApplicableFees] = useState<TransferFeeDescriptionType[]>([])

  const [selectedReason, setSelectedReason] = useState<DistributionReasonInfo | null>()
  const handleSelectReason = (reason: DistributionReasonInfo | null) => {
    setSelectedReason(reason)
  }

  const isConversionToConversion =
    formik.values.distributionReason === 'CONVERSION' && formik.values.contributionReason === 'CONVERSION'

  useEffect(() => {
    if (fromAccountIraConstraints) {
      if (fromAccountIraConstraints.retirementAccount && values.totalBalanceCheckbox) {
        setApplicableFees(['CLOSING'])
      } else {
        setApplicableFees([])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.totalBalanceCheckbox, fromAccountIraConstraints?.retirementAccount])

  return (
    <>
      <Box width='100%' maxW={MAX_FORM_WIDTH} marginTop={6} marginBottom={6}>
        <FromAccount displayedValue={accountFromDisplay} />
      </Box>
      <Box as='form' pb={4} maxW={MAX_FORM_WIDTH} width='100%' data-testid='cashJournalForm'>
        <Stack spacing={4}>
          <Stack spacing={6}>
            <Stack direction='row' spacing={4}>
              <Box width={COLUMN_WIDTH} maxW={MAX_COLUMN_WIDTH}>
                <LabeledInput
                  aria-label='to-account'
                  name='toAccount'
                  label={formatMessage('transfers.toAccount')}
                  className={'test-id-to-account'}
                  onChange={formik.handleChange}
                  isRequired
                  errorMessage={accountLookUpError || formik.errors.toAccount}
                  isInvalid={!!accountLookUpError || !!formik.errors.toAccount}
                  onBlur={formik.handleBlur}
                  value={formik.values.toAccount}
                  data-testid='toAccount'
                />
              </Box>
              <Box paddingTop={'26px'}>
                <Button
                  onClick={() => onLookupSubmit(formik.values.toAccount!)}
                  variant='secondary'
                  isLoading={loading}
                  type={'button'}
                  className={'test-id-lookup-btn'}
                >
                  Look-up
                </Button>
              </Box>
            </Stack>
          </Stack>

          {accountLookupData &&
            !isEmpty(accountLookupData.accountNumber) &&
            !accountLookUpError &&
            !isEmpty(accountLookupData.accountTitle) && <AccountLookupDetails account={accountLookupData} />}

          {!isEmpty(warningMessage) && (
            <Alert
              status='warning'
              title={warningMessageTitle}
              description={warningMessage}
              direction='column'
              showIcon={false}
            />
          )}

          <TransferAmount
            value={formik.values.transferAmountDisplay}
            isDisabled={formik.values.totalBalanceCheckbox}
            errorMessage={formik.errors.transferAmountDisplay || formik.errors.transferAmountRaw}
            handleBlur={formik.handleBlur}
            handleAmountChange={(e) => {
              formik.setValues({
                ...formik.values,
                transferAmountDisplay: numberInputFormatter(e.target.value, numberFormatter).display,
                transferAmountRaw: numberInputFormatter(e.target.value, numberFormatter).raw as number
              })
            }}
            isInvalid={
              (!!formik.errors.transferAmountRaw && formik.touched.transferAmountRaw) ||
              (!!formik.errors.transferAmountRaw && formik.values.totalBalanceCheckbox)
            }
            totalBalanceChecked={formik.values.totalBalanceCheckbox}
            handleCheckboxChange={formik.handleChange}
          />
          {totalBalanceNotAllowed && (
            <Text color='red.500'>This type of IRA account does not support a total amount transfer.</Text>
          )}

          {formVariation === JournalVariation.iraToIra && (
            <Stack spacing={6}>
              <IratoIraForm
                contributionReason={iraContributionConstraints?.validContributions!}
                distributionReason={iraDistributionConstraints?.validDistributionReasons!}
                saveData={handleSaveData}
                touched={touched}
                errors={formik.errors}
                showFedAndStateTax={isConversionToConversion}
              />
            </Stack>
          )}

          {formVariation === JournalVariation.iraToInd && (
            <IraDistributionForm
              formikSetValues={formik.setValues}
              formikValues={formik.values}
              formikBlur={formik.handleBlur}
              formikTouched={formik.touched}
              setFieldValue={formik.setFieldValue}
              setTouched={formik.setTouched}
              errors={formik.errors}
              selectedReason={selectedReason}
              handleSelectReason={handleSelectReason}
              distributionReasonOptions={iraDistributionConstraints?.validDistributionReasons}
              setFieldTouched={formik.setFieldTouched}
            />
          )}
          {formVariation === JournalVariation.indToIra && (
            <Stack spacing={4}>
              <IraContributionForm constraints={iraContributionConstraints} />
            </Stack>
          )}
          <Stack spacing={6} pb={6}>
            <TransferFrequency
              selectedRecurrence={values.transferRecurrence}
              selectedFrequency={values.transferFrequency}
              selectedWhenToSend={values.whenToSend}
              selectedScheduledDate={values.transferScheduledDate}
              selectedStartDate={values.transferStartDate}
              numberOfTransfers={values.numberOfTransfers}
              isTotalBalanceTransfer={values.totalBalanceCheckbox}
              isPreviousYearContribution={false}
            />
            {values.fromAccount && (
              <TransferFees
                accountNumber={values.fromAccount}
                applicableFees={applicableFees}
                feeTransferType={FeeTransferType.firstPartyJournal}
              />
            )}
          </Stack>
          <Box>
            <Button
              variant='primary'
              isDisabled={isNextDisabled || totalBalanceNotAllowed}
              type='button'
              className={'test-id-next-btn'}
              onClick={() => setIsModalShowing(true)}
            >
              Next
            </Button>
          </Box>
        </Stack>
      </Box>
    </>
  )
}
